import { Box, Grid, Text } from "@radix-ui/themes";
import cx from "classnames";

import { ErrorAction } from "../../root/customErrorComponent";

/**
 * Shown while a share-link MFA step is open under a tickets route.
 *
 * Shared by BOTH routes that can hit the step, because both of their reads park
 * it: the ticket route's error component loads a ticket through `getProject`
 * (`services/projects.ts:113`) and the tickets route's loads the folder listing
 * through `getFoldersAndTickets` (`:757`). Either read parks the step and raises
 * `EnterpriseMfaStepParkedError`, so a component owned by the ticket route
 * alone left the folder share link rendering "Unable to retrieve tickets
 * because an error occurred" behind the open TOTP dialog — wrong copy on a flow
 * that still worked.
 *
 * The action lives in `AuthenticationDialog` (the TOTP form), so there is
 * nothing to click here — the text names what the prompt is for and what to do
 * if it is gone.
 */
export function EnterpriseMfaStepPending() {
  return (
    <Grid
      align="center"
      justify="center"
      height="100vh"
      className={cx("w-full place-content-center p-12 text-center")}
    >
      <Box>
        {/*
          `role="alert"`, not `aria-labelledby`: a plain `div` has no implicit
          role, so an `aria-labelledby` on it names nothing and assistive
          technology ignores it. This page IS the whole screen while the parked
          step is open, so announcing it on appearance is the useful half — the
          `<h1>` still carries the heading. Same shape as the sibling
          `AiAccountBlocked` in `ErrorComponent.tsx`.
        */}
        <div
          role="alert"
          data-testid="share-link-mfa-step"
          className="mx-auto max-w-xl space-y-3"
        >
          <h1 className="p-5 text-3xl">Finish signing in to open this link</h1>
          <Text weight="medium">
            This shared link opens a workspace that requires multi-factor
            authentication.
          </Text>
          <Text>
            Enter the code from your authenticator app in the prompt to
            continue. If the prompt is no longer open, reload this page to start
            again.
          </Text>
          <ErrorAction withButtons={false} />
        </div>
      </Box>
    </Grid>
  );
}
